import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  SITE_URL,
  isStaging,
  getMetaForPath,
  canonicalUrl,
  pickMeta,
} from '../config/seo';

export interface SeoProps {
  /** Override title (e.g. project name). */
  title?: string;
  /** Override description. */
  description?: string;
  /** Override canonical path. */
  canonicalPath?: string;
  /** Override OG image URL (full or path). */
  ogImage?: string;
  /** JSON-LD script children (e.g. Person, CreativeWork). */
  jsonLd?: object | object[];
  /** OG type. */
  ogType?: 'website' | 'article';
}

export default function Seo({
  title: titleOverride,
  description: descriptionOverride,
  canonicalPath: canonicalPathOverride,
  ogImage: ogImageOverride,
  jsonLd,
  ogType: ogTypeOverride,
}: SeoProps) {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang = (i18n?.language?.startsWith('de') ? 'de' : 'en') as 'en' | 'de';

  const routeMeta = getMetaForPath(pathname, lang);
  const baseMeta = routeMeta
    ? pickMeta(routeMeta, lang)
    : { title: 'Julian Querido', description: 'Portfolio of Julian Querido, Frontend Engineer.' };

  const title = titleOverride ?? baseMeta.title;
  const description = descriptionOverride ?? baseMeta.description;
  const canonicalPath = canonicalPathOverride ?? routeMeta?.canonicalPath ?? pathname;
  const canonical = canonicalUrl(canonicalPath);
  const ogImage = ogImageOverride ?? routeMeta?.ogImage ?? `${SITE_URL}/profile-og.png`;
  const ogImageFull = ogImage.startsWith('http') ? ogImage : (ogImage.startsWith('/') ? `${SITE_URL}${ogImage}` : `${SITE_URL}/${ogImage}`);
  const ogType = ogTypeOverride ?? routeMeta?.ogType ?? 'website';

  const robotsContent = isStaging ? 'noindex,nofollow' : 'index,follow';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageFull} />
      <meta property="og:locale" content={lang === 'de' ? 'de_CH' : 'en_US'} />
      {lang === 'de' && <meta property="og:locale:alternate" content="en_US" />}
      {lang === 'en' && <meta property="og:locale:alternate" content="de_CH" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageFull} />

      {jsonLd != null && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(jsonLd)
              ? { '@context': 'https://schema.org', '@graph': jsonLd }
              : typeof (jsonLd as object & { '@context'?: string })['@context'] === 'string'
                ? jsonLd
                : { '@context': 'https://schema.org', ...(jsonLd as object) }
          )}
        </script>
      )}
    </Helmet>
  );
}
